import { Divider } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useFormState } from "react-final-form";
import RffFieldsRenderer from "../../../components/finalForm/shared/RffFieldsRenderer";
import RffGroup from "../../../components/finalForm/shared/RffGroup";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { loanQuickAppFormKeys as FMK } from "../../../constants/models/forms/loanQuickAppFormKeys";
import { selectOptionsRefinanceType } from "../../../constants/selectOptions/selectOptionsRefinanceType";
import { selectOptionsRequestedLoanTermMonths } from "../../../constants/selectOptions/selectOptionsRequestedLoanTermMonths";
import ProductDescription from "../components/ProductDescription";
const PMK = FMK.subjectProperties[0];
// Field definitions - reusable building blocks
const FIELD_DEFINITIONS = {
  address: (fieldName) => ({
    name: `${fieldName}.${PMK.address}`,
    label: "Property Address (Address, City, State, Zip)",
    type: "addressEditable",
    required: true,
  }),
  propertyType: (fieldName) => ({
    name: `${fieldName}.${PMK.propertyType}`,
    label: "Property Type",
    type: "selectToggle",
    options: ["SFR", "Multifamily", "Condo", "Townhome"],
    required: true,
  }),
  purchasePrice: (fieldName) => ({
    name: `${fieldName}.${PMK.purchasePrice}`,
    label: "Purchase Price ($)",
    type: "dollar",
    required: true,
  }),
  asIsValue: (fieldName) => ({
    name: `${fieldName}.${PMK.asIsValue}`,
    label: "As-Is Value ($)",
    type: "dollar",
  }),
  purchaseDate: (fieldName) => ({
    name: `${fieldName}.${PMK.purchaseDate}`,
    label: "Date Purchased",
    type: "date",
    required: true,
  }),
  constructionBudget: (fieldName) => ({
    name: `${fieldName}.${PMK.constructionBudget}`,
    label: "Construction Budget ($)",
    type: "dollar",
    required: true,
  }),
  arv: (fieldName) => ({
    name: `${fieldName}.${PMK.arv}`,
    label: "Estimated After Repair Value ($)",
    type: "dollar",
    required: true,
  }),
  annualTaxes: (fieldName) => ({
    name: `${fieldName}.${PMK.annualTaxes}`,
    label: "Annual Tax ($)",
    type: "dollar",
    required: true,
  }),
  annualInsurance: (fieldName) => ({
    name: `${fieldName}.${PMK.annualInsurance}`,
    label: "Annual Insurance ($)",
    type: "dollar",
    required: true,
  }),
  totalMonthlyRent: (fieldName) => ({
    name: `${fieldName}.${PMK.totalMonthlyRent}`,
    label: "Total Monthly Rents ($)",
    type: "dollar",
    required: true,
  }),
  annualHoaFees: (fieldName) => ({
    name: `${fieldName}.${PMK.annualHoaFees}`,
    label: "Monthly HOA (N/A if none)",
    type: "dollar",
    required: true,
  }),
  expectedClosingDate: (fieldName) => ({
    name: `${fieldName}.${PMK.expectedClosingDate}`,
    label: "Closing Date Requested",
    type: "date",
  }),
  hasSqftAddedOrUseChanged: (fieldName) => ({
    name: `${fieldName}.${PMK.hasSqftAddedOrUseChanged}`,
    label: "Any Sq Ft being added or Change of use?",
    type: "booleanToggle",
    required: true,
  }),
  additionalBasis: (fieldName) => ({
    name: `${fieldName}.${PMK.additionalBasis}`,
    label: "Has Any Work Already Been Completed? ($)",
    type: "dollar",
  }),

  hasApprovedPlansAndPermits: (fieldName) => ({
    name: `${fieldName}.${PMK.hasApprovedPlansAndPermits}`,
    label: "Do You Have Approved Plans and Permits?",
    type: "booleanToggle",
    required: true,
  }),
  requestedLoanTermMonths: (fieldName) => ({
    name: `${fieldName}.${PMK.requestedLoanTermMonths}`,
    label: "Term Requested",
    type: "selectToggle",
    options: selectOptionsRequestedLoanTermMonths,
    // options: ["6 months", "12 months", "18 months", "24 months"],
    required: true,
  }),
  refinanceType: (fieldName) => ({
    name: `${fieldName}.${PMK.refinanceType}`,
    label: "Cash Out or Rate & Term?",
    type: "selectToggle",
    options: selectOptionsRefinanceType,
    // options: ["Cash Out", "Rate and Term"],
    required: true,
  }),
};

// Configuration mapping: Product Type → Loan Purpose → Field Keys
const PRODUCT_FIELD_CONFIG = {
  "Fix and Flip": {
    Purchase: [
      PMK.address,
      PMK.purchasePrice,
      PMK.asIsValue,
      PMK.constructionBudget,
      PMK.arv,
      PMK.expectedClosingDate,
      PMK.hasSqftAddedOrUseChanged,
    ],
    "Refinance - Cash Out": [
      PMK.address,
      PMK.asIsValue,
      PMK.constructionBudget,
      PMK.arv,
      PMK.expectedClosingDate,
      PMK.hasSqftAddedOrUseChanged,
    ],
    "Refinance - No Cash Out": [
      PMK.address,
      PMK.asIsValue,
      PMK.constructionBudget,
      PMK.arv,
      PMK.expectedClosingDate,
      PMK.hasSqftAddedOrUseChanged,
    ],
  },
  "Fix and Rent": {
    Purchase: [
      PMK.address,
      PMK.purchasePrice,
      PMK.asIsValue,
      PMK.constructionBudget,
      PMK.arv,
      PMK.expectedClosingDate,
      PMK.totalMonthlyRent,
      PMK.hasSqftAddedOrUseChanged,
    ],
    "Refinance - Cash Out": [
      PMK.address,
      PMK.asIsValue,
      PMK.constructionBudget,
      PMK.arv,
      PMK.expectedClosingDate,
      PMK.totalMonthlyRent,
      PMK.hasSqftAddedOrUseChanged,
    ],
    "Refinance - No Cash Out": [
      PMK.address,
      PMK.asIsValue,
      PMK.constructionBudget,
      PMK.arv,
      PMK.expectedClosingDate,
      PMK.totalMonthlyRent,
      PMK.hasSqftAddedOrUseChanged,
    ],
  },
  "Stabilized Bridge": {
    Purchase: [
      PMK.address,
      PMK.propertyType,
      PMK.requestedLoanTermMonths,
      PMK.purchasePrice,
      PMK.asIsValue,
      PMK.additionalBasis,
      PMK.expectedClosingDate,
    ],
    "Refinance - Cash Out": [
      PMK.address,
      PMK.propertyType,
      PMK.requestedLoanTermMonths,
      PMK.purchaseDate,
      PMK.asIsValue,
      PMK.additionalBasis,
      PMK.expectedClosingDate,
    ],
    "Refinance - No Cash Out": [
      PMK.address,
      PMK.propertyType,
      PMK.requestedLoanTermMonths,
      PMK.purchaseDate,
      PMK.asIsValue,
      PMK.additionalBasis,
      PMK.expectedClosingDate,
    ],
  },
  "Ground Up Construction": {
    Purchase: [
      PMK.address,
      PMK.purchasePrice,
      PMK.asIsValue,
      PMK.constructionBudget,
      PMK.arv,
      PMK.expectedClosingDate,
    ],
    "Refinance - Cash Out": [
      PMK.address,
      PMK.purchaseDate,
      PMK.asIsValue,
      PMK.constructionBudget,
      PMK.arv,
      PMK.expectedClosingDate,
      PMK.hasApprovedPlansAndPermits,
    ],
    "Refinance - No Cash Out": [
      PMK.address,
      PMK.purchaseDate,
      PMK.asIsValue,
      PMK.constructionBudget,
      PMK.arv,
      PMK.expectedClosingDate,
      PMK.hasApprovedPlansAndPermits,
    ],
  },
  DSCR: {
    Purchase: [
      PMK.address,
      PMK.propertyType,
      PMK.purchasePrice,
      PMK.asIsValue,
      PMK.annualTaxes,
      PMK.annualInsurance,
      PMK.totalMonthlyRent,
      PMK.annualHoaFees,
      PMK.expectedClosingDate,
    ],
    "Refinance - Cash Out": [
      PMK.address,
      PMK.propertyType,
      PMK.purchaseDate,
      PMK.asIsValue,
      PMK.annualTaxes,
      PMK.annualInsurance,
      PMK.totalMonthlyRent,
      PMK.annualHoaFees,
      PMK.expectedClosingDate,
    ],
    "Refinance - No Cash Out": [
      PMK.address,
      PMK.propertyType,
      PMK.purchaseDate,
      PMK.asIsValue,
      PMK.annualTaxes,
      PMK.annualInsurance,
      PMK.totalMonthlyRent,
      PMK.annualHoaFees,
      PMK.expectedClosingDate,
    ],
  },
};

const LoanQuickAppStepTwoPropertyDetails = ({ stepName }) => {
  const { values } = useFormState();
  const fieldName = "subjectProperties[0]";

  // Get the appropriate fields based on current form values
  const fields = getFieldsForProduct(
    values?.loanProductType,
    values?.loanPurpose,
    fieldName,
  );

  return (
    <>
      {fields.length > 0 ? (
        <RffGroup
          title={stepName || "Property Details"}
          sx={{ position: "relative" }}
        >
          <ProductDescription />
          <RffFieldsRenderer fields={fields} />
        </RffGroup>
      ) : (
        <Txt sx={{ fontStyle: "italic", background: grey[50], p: 2 }}>
          Please select a loan product type and purpose to continue.
        </Txt>
      )}
    </>
  );
  // return (
  //   <Flx column>
  //     <ProductDescription />
  //     {fields.length < 0 ? (
  //       <RffGroup title={stepName || "Property Details"}>
  //         <RffFieldsRenderer fields={fields} />
  //       </RffGroup>
  //     ) : (
  //       <div>Please select a loan product type and purpose to continue.</div>
  //     )}
  //   </Flx>
  // );
};

/**
 * Gets the field configuration for a given product type and loan purpose
 */
const getFieldsForProduct = (productType, loanPurpose, fieldName) => {
  if (!productType || !loanPurpose) {
    return [];
  }

  const fieldKeys = PRODUCT_FIELD_CONFIG[productType]?.[loanPurpose];

  if (!fieldKeys) {
    console.warn(
      `No field configuration found for product: ${productType}, purpose: ${loanPurpose}`,
    );
    return [];
  }

  // Map field keys to actual field definitions
  return fieldKeys
    .map((key) => {
      const fieldDef = FIELD_DEFINITIONS[key];
      if (!fieldDef) {
        console.warn(`Field definition not found for key: ${key}`);
        return null;
      }
      return fieldDef(fieldName);
    })
    .filter(Boolean);
};

export default LoanQuickAppStepTwoPropertyDetails;
